/**
 * Resolve JSON Pointer paths against report input snapshots (trade bundles).
 * Bundles nest best_situations / worst_situations under market_condition_brackets;
 * LLM-generated pointers often skip that segment. Also unwraps { result: { stats } } envelopes.
 *
 * Trade-management TES pointers (`/tes_focus_slices/by_slice_id/...` or legacy `/tes_focus_slices/{n}/...`)
 * are resolved in `tesEvidencePointers.ts` against `tes_focus_slices` (not explorer sort order).
 */

import {
  parseTesFocusEvidencePointer,
  resolveTesFocusEvidencePointer,
  type EvidencePointerResolution,
} from "./tesEvidencePointers";

export type { EvidencePointerResolution };

export function decodeJsonPointerSegment(segment: string): string {
  return segment.replace(/~1/g, "/").replace(/~0/g, "~");
}

export function normalizeJsonPointer(pointer: string): string {
  let s = pointer.trim();
  if (s.startsWith("#")) s = s.slice(1);
  if (!s.startsWith("/")) s = `/${s}`;
  return s;
}

/** Use bundle body when snapshot is a job wrapper or similar. */
export function getEvidenceDocumentRoot(snapshot: unknown): unknown {
  if (!snapshot || typeof snapshot !== "object") return snapshot;
  const s = snapshot as Record<string, unknown>;
  const result = s.result;
  if (result && typeof result === "object" && result !== null && "stats" in (result as object)) {
    return result;
  }
  return snapshot;
}

/** Normalize TES pointers: JSON (`/stats/...`), dot (`stats.condition_analysis...`), or bare segments. */
export function evidencePointerToParts(pointer: string): string[] {
  let s = pointer.trim();
  if (!s) return [];
  if (s.startsWith("#")) s = s.slice(1);
  if (!s.startsWith("/") && s.includes(".")) {
    return s.split(".").filter(Boolean).map(decodeJsonPointerSegment);
  }
  return normalizeJsonPointer(s)
    .split("/")
    .filter(Boolean)
    .map(decodeJsonPointerSegment);
}

function pointerFallbacks(normalized: string): string[] {
  const out: string[] = [normalized];

  // directional_condition_analysis/{long|short}/best_situations|worst_situations/...
  const dir = normalized.match(
    /^(\/(?:stats|analysis_evidence)\/condition_analysis\/directional_condition_analysis\/(?:long|short))\/(best_situations|worst_situations)(\/.*)?$/
  );
  if (dir) {
    out.push(`${dir[1]}/market_condition_brackets/${dir[2]}${dir[3] ?? ""}`);
  }

  // Aggregate condition_analysis (omit directional): .../best_situations/...
  const agg = normalized.match(
    /^(\/(?:stats|analysis_evidence)\/condition_analysis)\/(best_situations|worst_situations)(\/.*)?$/
  );
  if (agg) {
    out.push(`${agg[1]}/market_condition_brackets/${agg[2]}${agg[3] ?? ""}`);
  }

  return out;
}

function statsConditionAnalysisMirrorPaths(normalized: string): string[] {
  const out: string[] = [];
  if (normalized.startsWith("/stats/condition_analysis/")) {
    out.push(normalized.replace("/stats/condition_analysis/", "/analysis_evidence/condition_analysis/"));
  }
  if (normalized.startsWith("/analysis_evidence/condition_analysis/")) {
    out.push(normalized.replace("/analysis_evidence/condition_analysis/", "/stats/condition_analysis/"));
  }
  return out;
}

function pointerToNormalizedCandidates(pointer: string): string[] {
  const parts = evidencePointerToParts(pointer);
  if (!parts.length) return pointerFallbacks(normalizeJsonPointer(pointer));

  let normalized: string;
  if (parts[0] === "stats") {
    normalized = `/stats/${parts.slice(1).join("/")}`;
  } else if (parts[0] === "analysis_evidence") {
    normalized = `/analysis_evidence/${parts.slice(1).join("/")}`;
  } else if (parts[0] === "condition_analysis") {
    normalized = `/stats/condition_analysis/${parts.slice(1).join("/")}`;
  } else if (parts[0] === "management_candidates" || parts[0] === "tes_focus_slices") {
    normalized = `/${parts.join("/")}`;
  } else {
    normalized = normalizeJsonPointer(pointer);
  }

  const candidates = new Set<string>();
  for (const path of pointerFallbacks(normalized)) {
    candidates.add(path);
    for (const mirror of statsConditionAnalysisMirrorPaths(path)) {
      candidates.add(mirror);
      for (const fb of pointerFallbacks(mirror)) candidates.add(fb);
    }
  }
  return Array.from(candidates);
}

export function resolveJsonPointer(root: unknown, pointer: string): unknown {
  if (!root || typeof pointer !== "string") return undefined;
  const normalized = normalizeJsonPointer(pointer);

  const parts = normalized
    .split("/")
    .slice(1)
    .map(decodeJsonPointerSegment);

  let current: unknown = root;
  for (const part of parts) {
    if (current == null) return undefined;
    if (Array.isArray(current) && /^\d+$/.test(part)) {
      current = current[Number(part)];
      continue;
    }
    if (typeof current !== "object") return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

export type ResolveEvidencePointerOptions = {
  /** Finding’s cited segment — used to warn on legacy numeric index mismatches. */
  primarySliceId?: string;
};

/** Try exact pointer, then known shorthand paths used in generated evidence_pointers. */
export function resolveEvidencePointerWithMeta(
  snapshot: unknown,
  pointer: string,
  options?: ResolveEvidencePointerOptions
): EvidencePointerResolution {
  const normalized = normalizeJsonPointer(pointer);

  if (parseTesFocusEvidencePointer(normalized)) {
    return resolveTesFocusEvidencePointer(snapshot, pointer, options?.primarySliceId);
  }

  const root = getEvidenceDocumentRoot(snapshot);
  for (const candidate of pointerToNormalizedCandidates(pointer)) {
    const value = resolveJsonPointer(root, candidate);
    if (value !== undefined) return { value };
  }
  return { value: undefined };
}

/** Try exact pointer, then known shorthand paths used in generated evidence_pointers. */
export function resolveEvidencePointer(
  snapshot: unknown,
  pointer: string,
  options?: ResolveEvidencePointerOptions
): unknown {
  return resolveEvidencePointerWithMeta(snapshot, pointer, options).value;
}
